import React, { useState, useEffect } from 'react';
import { 
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { API_ENDPOINTS } from '../../constants/api';
import useNotification from '../../hooks/useNotification';
import Button from '../ui/Button';
import Input from '../ui/Input';
import axiosInstance from '../../api/Axios';
import TelegramSuccessModal from '../modals/TelegramSuccessModal';

const TelegramSettings = () => {
  const { showSuccess, showError } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState({
    admin: false,
    user: false
  });
  const [isInitializing, setIsInitializing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSendingTestMessage, setIsSendingTestMessage] = useState({
    admin: false,
    user: false
  });
  const [isSendingTestReport, setIsSendingTestReport] = useState(false);
  
  // Состояние настроек
  const [settings, setSettings] = useState({
    adminBotToken: '',
    userBotToken: '',
    groupId: '',
    threadId: 8,
    bugThreadId: 8,
    enableTaskNotifications: true,
    enableReportNotifications: true,
    enableErrorNotifications: true,
    loggerChatId: ''
  });

  // Состояние статуса подключения
  const [connectionStatus, setConnectionStatus] = useState({
    admin: null,
    user: null
  });

  // API хуки
  const { 
    data: settingsData, 
    loading: isLoadingSettings, 
    fetchData: getSettings,
    postData: updateSettings,
    putData: putSettings
  } = useApi(API_ENDPOINTS.TELEGRAM_SETTINGS);

  const { fetchData: testConnection } = useApi(API_ENDPOINTS.TELEGRAM_SETTINGS_TEST);
  const { fetchData: getStats } = useApi(API_ENDPOINTS.TELEGRAM_SETTINGS_STATS);
  const { postData: initializeService } = useApi(API_ENDPOINTS.TELEGRAM_SETTINGS_INITIALIZE);

  // Загружаем настройки при монтировании компонента
  useEffect(() => {
    loadSettings();
  }, []);

  // Обновляем локальное состояние при получении данных
  useEffect(() => {
    if (settingsData) {
      setSettings(prevSettings => ({
        ...prevSettings,
        ...settingsData
      }));
    }
  }, [settingsData]);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      await getSettings();
    } catch (error) {
      showError('Ошибка загрузки настроек Telegram');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setIsLoading(true);
      await putSettings(settings);
      showSuccess('Настройки Telegram успешно сохранены');
      await loadSettings(); // Перезагружаем настройки
    } catch (error) {
      showError('Ошибка сохранения настроек');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestConnection = async (botType) => {
    try {
      setIsTesting(prev => ({
        ...prev,
        [botType]: true
      }));
      // Динамический запрос напрямую через axiosInstance
      const response = await axiosInstance.get(`${API_ENDPOINTS.TELEGRAM_SETTINGS_TEST}/${botType}`);
      if (response.data.success) {
        setConnectionStatus(prev => ({
          ...prev,
          [botType]: true
        }));
        showSuccess(`Подключение к боту ${botType === 'admin' ? 'администратора' : 'пользователя'} успешно`);
      } else {
        setConnectionStatus(prev => ({
          ...prev,
          [botType]: false
        }));
        showError(`Ошибка подключения к боту ${botType === 'admin' ? 'администратора' : 'пользователя'}`);
      }
    } catch (error) {
      setConnectionStatus(prev => ({
        ...prev,
        [botType]: false
      }));
      showError(`Ошибка тестирования подключения к боту ${botType === 'admin' ? 'администратора' : 'пользователя'}`);
    } finally {
      setIsTesting(prev => ({
        ...prev,
        [botType]: false
      }));
    }
  };

  const handleInitializeService = async () => {
    try {
      setIsInitializing(true);
      await initializeService();
      setShowSuccessModal(true);
      showSuccess('Сервис Telegram успешно инициализирован');
    } catch (error) {
      showError('Ошибка инициализации сервиса Telegram');
    } finally {
      setIsInitializing(false);
    }
  };

  const handleSendTestMessage = async (botType) => {
    try {
      setIsSendingTestMessage(prev => ({
        ...prev,
        [botType]: true
      }));

      const response = await axiosInstance.post(API_ENDPOINTS.TELEGRAM_SETTINGS_SEND_TEST_MESSAGE, {
        botType,
        message: `🧪 Тестовое сообщение от ${botType === 'admin' ? 'административного' : 'пользовательского'} бота\n\n⏰ Время отправки: ${new Date().toLocaleString('ru-RU')}\n👤 Отправитель: ${localStorage.getItem('username') || 'Система'}`
      });

      if (response.data.success) {
        showSuccess(`Тестовое сообщение успешно отправлено через ${botType === 'admin' ? 'административного' : 'пользовательского'} бота`);
      } else {
        showError(`Ошибка отправки тестового сообщения: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error sending test message:', error);
      showError(`Ошибка отправки тестового сообщения: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsSendingTestMessage(prev => ({
        ...prev,
        [botType]: false
      }));
    }
  };

  const handleSendTestReport = async () => {
    try {
      setIsSendingTestReport(true);

      const response = await axiosInstance.post(API_ENDPOINTS.TELEGRAM_SETTINGS_SEND_TEST_REPORT);

      if (response.data.success) {
        showSuccess('Тестовый отчет успешно отправлен');
      } else {
        showError(`Ошибка отправки тестового отчета: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error sending test report:', error);
      showError(`Ошибка отправки тестового отчета: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsSendingTestReport(false);
    }
  };

  const getConnectionStatusIcon = (status) => {
    if (status === null) return <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" />;
    if (status) return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    return <XCircleIcon className="h-5 w-5 text-red-500" />;
  };

  const getConnectionStatusText = (status) => {
    if (status === null) return 'Не проверено';
    if (status) return 'Подключено';
    return 'Ошибка подключения';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center mb-6">
        <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-500 mr-3" />
        <h2 className="text-lg font-semibold text-gray-900">Настройки Telegram</h2>
      </div>

      {isLoadingSettings ? (
        <div className="flex items-center justify-center py-8">
          <ArrowPathIcon className="h-6 w-6 text-blue-500 animate-spin" />
          <span className="ml-2 text-gray-600">Загрузка настроек...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Токены ботов */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Токен административного бота
              </label>
              <Input
                type="password"
                value={settings.adminBotToken}
                onChange={(e) => handleInputChange('adminBotToken', e.target.value)}
                placeholder="bot123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                className="w-full"
              />
              <div className="flex items-center mt-2">
                {getConnectionStatusIcon(connectionStatus.admin)}
                <span className="ml-2 text-sm text-gray-600">
                  {getConnectionStatusText(connectionStatus.admin)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleTestConnection('admin')}
                  disabled={isTesting.admin}
                  className="ml-auto"
                >
                  {isTesting.admin ? 'Проверка...' : 'Тест'}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Токен пользовательского бота
              </label>
              <Input
                type="password"
                value={settings.userBotToken}
                onChange={(e) => handleInputChange('userBotToken', e.target.value)}
                placeholder="bot123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                className="w-full"
              />
              <div className="flex items-center mt-2">
                {getConnectionStatusIcon(connectionStatus.user)}
                <span className="ml-2 text-sm text-gray-600">
                  {getConnectionStatusText(connectionStatus.user)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleTestConnection('user')}
                  disabled={isTesting.user}
                  className="ml-auto"
                >
                  {isTesting.user ? 'Проверка...' : 'Тест'}
                </Button>
              </div>
            </div>
          </div>

          {/* Настройки группы */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID группы
              </label>
              <Input
                type="text"
                value={settings.groupId}
                onChange={(e) => handleInputChange('groupId', e.target.value)}
                placeholder="-1002229198165"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID темы (thread ID) для задач
              </label>
              <Input
                type="number"
                value={settings.threadId}
                onChange={(e) => handleInputChange('threadId', parseInt(e.target.value) || 8)}
                placeholder="8"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID темы для пересылки багов (#баг)
              </label>
              <Input
                type="number"
                value={settings.bugThreadId}
                onChange={(e) => handleInputChange('bugThreadId', parseInt(e.target.value) || 8)}
                placeholder="8"
                className="w-full"
              />
            </div>
          </div>

          {/* Настройки уведомлений */}
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-3">Настройки уведомлений</h3>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.enableTaskNotifications}
                  onChange={(e) => handleInputChange('enableTaskNotifications', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Уведомления о задачах</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.enableReportNotifications}
                  onChange={(e) => handleInputChange('enableReportNotifications', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Уведомления о отчетах</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.enableErrorNotifications}
                  onChange={(e) => handleInputChange('enableErrorNotifications', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Уведомления об ошибках</span>
              </label>
            </div>
          </div>

          {/* Chat ID для логгера */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chat ID для логгера
            </label>
            <Input
              type="text"
              value={settings.loggerChatId}
              onChange={(e) => handleInputChange('loggerChatId', e.target.value)}
              placeholder="487562279"
              className="w-full"
            />
          </div>

          {/* Кнопки действий */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
            <Button
              onClick={handleSaveSettings}
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? 'Сохранение...' : 'Сохранить настройки'}
            </Button>

            <Button
              onClick={handleInitializeService}
              disabled={isInitializing}
              variant="outline"
              className="flex-1"
            >
              {isInitializing ? 'Инициализация...' : 'Инициализировать сервис'}
            </Button>
          </div>

          {/* Кнопки тестовой отправки сообщений */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-md font-medium text-gray-900 mb-3">Тестовая отправка сообщений</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={() => handleSendTestMessage('admin')}
                disabled={isSendingTestMessage.admin}
                variant="outline"
                className="flex items-center justify-center"
              >
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                {isSendingTestMessage.admin ? 'Отправка...' : 'Отправить тестовое сообщение (Админ бот)'}
              </Button>

              <Button
                onClick={() => handleSendTestMessage('user')}
                disabled={isSendingTestMessage.user}
                variant="outline"
                className="flex items-center justify-center"
              >
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                {isSendingTestMessage.user ? 'Отправка...' : 'Отправить тестовое сообщение (Пользовательский бот)'}
              </Button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Админ бот отправит сообщение в логгер чат, пользовательский бот - в группу
            </p>
          </div>

          {/* Кнопка тестовой отправки отчета */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-md font-medium text-gray-900 mb-3">Тестовая отправка отчета</h3>
            <Button
              onClick={handleSendTestReport}
              disabled={isSendingTestReport}
              variant="outline"
              className="flex items-center justify-center w-full"
            >
              <PaperAirplaneIcon className="h-4 w-4 mr-2" />
              {isSendingTestReport ? 'Отправка отчета...' : 'Отправить тестовый отчет разработчиков'}
            </Button>
            <p className="text-sm text-gray-600 mt-2">
              Отправит ежедневный отчет по разработчикам (такой же, как отправляется автоматически в 12:00)
            </p>
          </div>

          {/* Информация */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Как настроить Telegram:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Создайте бота через @BotFather в Telegram</li>
              <li>• Получите токен бота и вставьте его в соответствующее поле</li>
              <li>• Добавьте бота в группу и получите ID группы</li>
              <li>• Настройте права бота в группе (отправка сообщений)</li>
              <li>• Протестируйте подключение кнопкой "Тест"</li>
            </ul>
          </div>
        </div>
      )}

      {/* Модальное окно успешной инициализации */}
      <TelegramSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </div>
  );
};

export default TelegramSettings; 